import React, { useState, useEffect, useMemo } from "react";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Typography,
  alpha,
  keyframes,
  useTheme,
} from "@mui/material";
import axios from "axios";

import applicationApi from "@domains/application/module/api/applicationApi";
import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "@platform/constants/shared-constants";
import { getEmptyStateSx } from "src/shell/styles/themeUtils";

import { formatRelativeTime, formatDuration } from "./shared/pipeline-helpers";

// ── Types ──────────────────────────────────────────────────────────────────────

interface PipelineRunRow {
  id: string;
  sagaId: string;
  systemId: number;
  systemAlias: string;
  trigger: string;
  status: "queued" | "in_progress" | "completed" | "failed";
  sha: string | null;
  branch: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

type RunFilter = "all" | "running" | "failed" | "completed";

export interface RunsListProps {
  stackAlias: string;
  deployMode: string;
  orgAlias: string;
  selectedRunId: string | null;
  onSelectRun: (sagaId: string | null) => void;
  /** Incremented each time a pipeline_run WS event arrives; triggers a re-fetch. */
  refreshSignal?: number;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const TRIGGER_LABELS: Record<string, string> = {
  build: "Build",
  deploy: "Deploy",
  build_and_deploy: "Build + Deploy",
  retry_build: "Retry Build",
  redeploy: "Redeploy",
};

const FILTER_LABELS: Record<RunFilter, string> = {
  all: "All",
  running: "Running",
  failed: "Failed",
  completed: "Completed",
};

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Status circle colors
const STATUS_CIRCLE: Record<string, { bg: string; icon: React.ReactElement }> =
  {
    completed: {
      bg: "rgba(16,185,129,0.15)",
      icon: <CheckIcon sx={{ fontSize: 16, color: "#10b981" }} />,
    },
    in_progress: {
      bg: "rgba(59,130,246,0.15)",
      icon: (
        <SyncIcon
          sx={{
            fontSize: 16,
            color: "#3b82f6",
            animation: `${spin} 1.5s linear infinite`,
          }}
        />
      ),
    },
    failed: {
      bg: "rgba(239,68,68,0.15)",
      icon: <CloseIcon sx={{ fontSize: 16, color: "#ef4444" }} />,
    },
    queued: {
      bg: "rgba(148,163,184,0.15)",
      icon: <HourglassEmptyIcon sx={{ fontSize: 16, color: "#94A3B8" }} />,
    },
    pending: {
      bg: "rgba(148,163,184,0.15)",
      icon: <HourglassEmptyIcon sx={{ fontSize: 16, color: "#94A3B8" }} />,
    },
  };

// ── Helpers ────────────────────────────────────────────────────────────────────

function shortSha(sha: string | null): string {
  if (!sha) return "";
  return sha.length > 8 ? sha.slice(0, 8) : sha;
}

function computeDurationSec(run: PipelineRunRow): number | null {
  if (run.status === "in_progress" || run.status === "queued") return null;
  if (!run.completedAt) return null;
  const start = new Date(run.createdAt).getTime();
  const end = new Date(run.completedAt).getTime();
  return Math.round((end - start) / 1000);
}

// ── Component ──────────────────────────────────────────────────────────────────

export function PipelineRunsList({
  stackAlias,
  deployMode,
  orgAlias: _orgAlias,
  selectedRunId,
  onSelectRun,
  refreshSignal,
}: RunsListProps): React.ReactElement {
  const theme = useTheme();
  const [runs, setRuns] = useState<PipelineRunRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<RunFilter>("all");
  const [triggerLoading, setTriggerLoading] = useState(false);

  const isRushaOwnedBuild =
    deployMode === "rusha_managed" || deployMode === "rusha_build_ci_deploy";

  // Fetch runs
  useEffect(() => {
    const token = orgSessionManager.getToken() ?? "";
    setLoading(true);
    axios
      .get<{ runs: PipelineRunRow[]; total: number }>(
        `${apiBaseUrls.BACKEND}/api/v1/applications/${stackAlias}/pipeline-runs`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => setRuns(res.data.runs))
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, [stackAlias, refreshSignal]);

  // Filter runs
  const filteredRuns = useMemo(() => {
    if (filter === "all") return runs;
    const statusMap: Record<RunFilter, string[]> = {
      all: [],
      running: ["in_progress", "queued"],
      failed: ["failed"],
      completed: ["completed"],
    };
    const matchStatuses = statusMap[filter];
    return runs.filter((r) => matchStatuses.includes(r.status));
  }, [runs, filter]);

  // Filter counts
  const filterCounts = useMemo(() => {
    const counts: Record<RunFilter, number> = {
      all: runs.length,
      running: runs.filter(
        (r) => r.status === "in_progress" || r.status === "queued",
      ).length,
      failed: runs.filter((r) => r.status === "failed").length,
      completed: runs.filter((r) => r.status === "completed").length,
    };
    return counts;
  }, [runs]);

  // Trigger new run
  const handleTriggerRun = async () => {
    setTriggerLoading(true);
    try {
      await applicationApi.buildAndDeploy(stackAlias);
      setTimeout(() => {
        const token = orgSessionManager.getToken() ?? "";
        void axios
          .get<{ runs: PipelineRunRow[] }>(
            `${apiBaseUrls.BACKEND}/api/v1/applications/${stackAlias}/pipeline-runs`,
            { headers: { Authorization: `Bearer ${token}` } },
          )
          .then((res) => setRuns(res.data.runs));
      }, 1500);
    } finally {
      setTriggerLoading(false);
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Stack spacing={1.5}>
      {/* Header: title + Run Pipeline button */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: 18 }}>
          Pipeline Runs
        </Typography>
        {isRushaOwnedBuild && (
          <Button
            variant="contained"
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={handleTriggerRun}
            disabled={triggerLoading}
            sx={{ borderRadius: 1.5, textTransform: "none" }}
          >
            Run Pipeline
          </Button>
        )}
      </Stack>

      {/* Filter chips */}
      <Stack direction="row" spacing={0.5}>
        {(["all", "running", "failed", "completed"] as RunFilter[]).map((f) => (
          <Chip
            key={f}
            label={`${FILTER_LABELS[f]} (${filterCounts[f]})`}
            size="small"
            variant={filter === f ? "filled" : "outlined"}
            color={filter === f ? "primary" : "default"}
            onClick={() => setFilter(f)}
            sx={{ cursor: "pointer", textTransform: "none" }}
          />
        ))}
      </Stack>

      {/* Run list / empty state */}
      {filteredRuns.length === 0 ? (
        <Box sx={getEmptyStateSx(theme)}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2,
              margin: "0 auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <SyncIcon
              sx={{ fontSize: 28, color: "primary.main", opacity: 0.5 }}
            />
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
            {runs.length === 0
              ? "No pipeline runs"
              : "No runs match the selected filter"}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 400, mx: "auto" }}
          >
            {runs.length === 0
              ? "This stack has not been deployed yet. Run a pipeline to see deploy history."
              : "Try a different filter to see more runs."}
          </Typography>
          {runs.length === 0 && isRushaOwnedBuild && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={handleTriggerRun}
              disabled={triggerLoading}
              sx={{ borderRadius: 1.5, textTransform: "none" }}
            >
              Run pipeline
            </Button>
          )}
        </Box>
      ) : (
        <Stack spacing={0}>
          {filteredRuns.slice(0, 30).map((run, index) => {
            const circle = STATUS_CIRCLE[run.status] ?? STATUS_CIRCLE.pending;
            const isSelected = run.sagaId === selectedRunId;
            const isLatest = index === 0;
            const durationSec = computeDurationSec(run);

            return (
              <Box
                key={run.sagaId}
                onClick={() => onSelectRun(run.sagaId)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 1.5,
                  px: 2,
                  gap: 1.5,
                  borderRadius: 1.5,
                  cursor: "pointer",
                  "&:hover": { bgcolor: alpha("#3b82f6", 0.04) },
                  ...(isSelected && {
                    bgcolor: alpha("#3b82f6", 0.08),
                    borderLeft: `3px solid #3b82f6`,
                    pl: 1.625,
                  }),
                }}
              >
                {/* Status circle */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: circle.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {circle.icon}
                </Box>

                {/* Title + subtitle */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
                    {TRIGGER_LABELS[run.trigger] ?? run.trigger}
                    {run.sha && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 12,
                          color: "text.disabled",
                          fontFamily: "monospace",
                          ml: 1,
                        }}
                      >
                        {shortSha(run.sha)}
                      </Typography>
                    )}
                    {isLatest && (
                      <Chip
                        label="Latest"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 16, fontSize: "0.55rem", ml: 1 }}
                      />
                    )}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}
                  >
                    {run.systemAlias}
                    {run.branch ? ` · push to ${run.branch}` : ""}
                  </Typography>
                </Box>

                {/* Branch badge */}
                {run.branch && (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                      fontSize: 12,
                      color: "text.secondary",
                      flexShrink: 0,
                    }}
                  >
                    {run.branch}
                  </Box>
                )}

                {/* Duration */}
                {durationSec !== null && (
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "text.secondary",
                      flexShrink: 0,
                    }}
                  >
                    {formatDuration(durationSec)}
                  </Typography>
                )}

                {/* Time ago */}
                <Typography
                  sx={{ fontSize: 13, color: "text.secondary", flexShrink: 0 }}
                >
                  {formatRelativeTime(run.createdAt)}
                </Typography>

                {/* Per-row action buttons */}
                <Stack direction="row" spacing={0.75} flexShrink={0}>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      textTransform: "none",
                      fontSize: 11,
                      borderRadius: 1,
                      minWidth: "auto",
                      px: 1,
                      py: 0.25,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectRun(run.sagaId);
                    }}
                  >
                    Logs
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      textTransform: "none",
                      fontSize: 11,
                      borderRadius: 1,
                      minWidth: "auto",
                      px: 1,
                      py: 0.25,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleTriggerRun();
                    }}
                  >
                    Rerun
                  </Button>
                  {run.status === "completed" && (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: "none",
                        fontSize: 11,
                        borderRadius: 1,
                        minWidth: "auto",
                        px: 1,
                        py: 0.25,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Promote
                    </Button>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
